import type { CegoXmpp, XmppOnOpts, XmppOnResult } from '@cego/xmpp'
import type { ShallowRef } from 'vue'
import { useXmpp, onBeforeUnmount, onMounted, shallowRef } from '#imports'

export type UseXmppListenerCallback<TMessage = XmppOnResult['json']> = (message: TMessage, result: XmppOnResult) => void | Promise<void>
export interface UseXmppListenerReturn<TMessage> {
    data: ShallowRef<TMessage | undefined>
}

export const useXmppListener = <TMessage = XmppOnResult['json']>(filters: XmppOnOpts, callback?: UseXmppListenerCallback<TMessage>): UseXmppListenerReturn<TMessage> => {
    if (import.meta.server) {
        return {
            data: shallowRef()
        }
    }

    let xmpp: InstanceType<typeof CegoXmpp> | null = null
    let listener: any = null

    const data = shallowRef<TMessage>()

    onMounted(() => {
        onEnabled() // async but fire-and-forget is fine here
    })

    onBeforeUnmount(() => {
        onDisabled()
    })

    function onEnabled() {
        const { ensureInit } = useXmpp()

        return ensureInit().then((instance) => {
            if (!instance) return

            xmpp = instance

            listener = xmpp.on(filters, async (result: XmppOnResult) => {
                const json = result.json as TMessage
                data.value = json

                if (callback) {
                    await callback(json, result)
                }
            })
        })
    }

    function onDisabled() {
        if (xmpp && listener) {
            xmpp.off(listener)

            listener = null
        }
    }

    return { data }
}
